import React, { type HTMLAttributes, useRef } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import c from 'classnames';

import { Icon } from '@uncinc/uncinc-react-kitchen-sink';

import SearchAllContent from '../../SearchAllContent';
import ButtonWrapper from '../../ButtonWrapper';

import { createSearchUrl } from '../../../helpers/createSearchURL';
import { APP_OPTIONS, SEARCH_PATH } from '../../../config/constants';

import './style.scss';

interface SoloSearchProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  collapsible?: boolean;
  toggleOverlay?: (onlyClose?: boolean) => void;
}

const SoloSearch: React.FC<SoloSearchProps> = ({
  collapsible = false,
  toggleOverlay = () => {},
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const isSearchCollapsed = useRef<boolean>(true);

  const SoloSearchClassNames = c(
    'SoloSearch',
    {
      'is-collapsible': collapsible,
      'is-collapsed': collapsible && isSearchCollapsed.current,
    },
  );

  const toggleSearchBar = () => {
    isSearchCollapsed.current = !isSearchCollapsed.current;
    if (isSearchCollapsed.current) {
      wrapperRef.current?.classList.add('is-collapsed');
    } else {
      wrapperRef.current?.classList.remove('is-collapsed');
    }
  };

  const onSearchSubmit = (query: string) => {
    const url = createSearchUrl({
      pathname: SEARCH_PATH,
      index: APP_OPTIONS.parameters.searchkitIndexAllContent,
      value: query,
    });

    try {
      history.push(url);
      if (collapsible) {
        toggleSearchBar();
      }
      if (!collapsible) {
        toggleOverlay(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div ref={wrapperRef} className={SoloSearchClassNames}>
      {!collapsible && (
        <p className="SoloSearch__label body--large">
          {t('pages.search.solo_body')}
        </p>
      )}
      {collapsible && (
        <ButtonWrapper
          className="SoloSearch__collapse-button__open"
          onClick={toggleSearchBar}
          inverted
        >
          <Icon flair="outlined" name="search" />
        </ButtonWrapper>
      )}
      <SearchAllContent className="SoloSearch__search" onlyFetchWithQuery>
        <SearchAllContent.SearchBar onSubmit={onSearchSubmit} />
        {collapsible && (
          <ButtonWrapper
            className="SoloSearch__collapse-button__close"
            onClick={toggleSearchBar}
            inverted
          >
            <Icon flair="outlined" name="close" />
          </ButtonWrapper>
        )}
      </SearchAllContent>
    </div>
  );
};

export default SoloSearch;
